import React, { useState, useContext } from "react";
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  Stack,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { useAICalendar } from "contexts/AICalendarContext";
import _request from "actions/_request";
import AppContext from "AppContext";
import BuyCreditsModal from "components/BuyCreditsModal";
import i18n from "i18";

export default function Step1Goals() {
  const {
    nextStep,
    setStrategy,
    setBusinessContext,
    setTimeRange,
    setSelectedAccounts,
    setSelectedAccountDetails,
  } = useAICalendar();
  const { metaData, refreshCredits } = useContext(AppContext);
  const isTrial = metaData?.ai_credits ? metaData.ai_credits.monthly_allowance === 0 : false;
  const [buyCreditsOpen, setBuyCreditsOpen] = useState(false);

  // Form state
  const [objectives, setObjectives] = useState([]);
  const [timePeriodType, setTimePeriodType] = useState("week");
  const [startDate, setStartDate] = useState(() => moment().startOf("day"));
  const [endDate, setEndDate] = useState(null);
  const [industry, setIndustry] = useState("");
  const [targetAudience, setTargetAudience] = useState("");
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [accounts, setAccounts] = useState([]);

  // Load accounts on mount
  React.useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      const resp = await _request.accounts.getAll();
      const items = resp?.isOk && resp?.data?.items ? resp.data.items : [];
      setAccounts(items);
      // Auto-select the only account so the user doesn't have to click into a single-option dropdown
      if (items.length === 1) {
        setSelectedAccountIds([items[0].id]);
      }
    } catch (error) {
      console.error("Failed to load accounts:", error);
      setAccounts([]);
    }
  };

  const objectiveOptions = [
    { value: "brand_awareness", label: i18n.t("aiCalendarWizard.objectives.brandAwareness") },
    { value: "engagement", label: i18n.t("aiCalendarWizard.objectives.engagement") },
    { value: "conversions", label: i18n.t("aiCalendarWizard.objectives.conversions") },
    { value: "traffic", label: i18n.t("aiCalendarWizard.objectives.traffic") },
    { value: "community_building", label: i18n.t("aiCalendarWizard.objectives.communityBuilding") },
  ];

  const handleObjectiveToggle = (value) => {
    setObjectives((prev) =>
      prev.includes(value)
        ? prev.filter((o) => o !== value)
        : [...prev, value]
    );
  };

  const handleSubmit = async () => {
    if (objectives.length === 0) {
      setLocalError(i18n.t("aiCalendarWizard.errors.noObjectives"));
      return;
    }
    if (!startDate || !startDate.isValid?.()) {
      setLocalError(i18n.t("aiCalendarWizard.errors.noStartDate"));
      return;
    }
    if (timePeriodType === "custom") {
      if (!endDate || !endDate.isValid?.()) {
        setLocalError(i18n.t("aiCalendarWizard.errors.noEndDate"));
        return;
      }
      if (!endDate.isAfter(startDate, "day")) {
        setLocalError(i18n.t("aiCalendarWizard.errors.endBeforeStart"));
        return;
      }
    }
    if (!industry || !targetAudience) {
      setLocalError(i18n.t("aiCalendarWizard.errors.missingContext"));
      return;
    }
    if (selectedAccountIds.length === 0) {
      setLocalError(i18n.t("aiCalendarWizard.errors.noAccounts"));
      return;
    }

    setLocalError(null);
    setLoading(true);

    try {
      let resolvedEnd;
      let resolvedType;
      if (timePeriodType === "week") {
        resolvedEnd = startDate.clone().add(6, "days");
        resolvedType = "week";
      } else if (timePeriodType === "month") {
        resolvedEnd = startDate.clone().add(30, "days");
        resolvedType = "month";
      } else {
        resolvedEnd = endDate;
        resolvedType = resolvedEnd.diff(startDate, "days") <= 14 ? "week" : "month";
      }

      const params = {
        objectives,
        time_period: {
          type: resolvedType,
          start_date: startDate.format("YYYY-MM-DD"),
          end_date: resolvedEnd.format("YYYY-MM-DD"),
        },
        business_context: {
          industry,
          target_audience: targetAudience,
          account_ids: selectedAccountIds,
        },
      };

      const response = await _request.aiCalendar.analyzeGoals(params);

      if (response?.isInsufficientCredits) {
        setBuyCreditsOpen(true);
      } else if (response?.isOk && response?.data) {
        // allowed_scheduled_posts < 0 is the project-wide sentinel for unlimited.
        // Guard against older server builds that return can_proceed=false for -1 (OP#1987).
        const packageLimits = response.data.package_limits;
        const isUnlimited = packageLimits?.unlimited === true
          || packageLimits?.allowed_scheduled_posts < 0;
        if (packageLimits && !packageLimits.can_proceed && !isUnlimited) {
          setLocalError(packageLimits.warning || i18n.t("aiCalendarWizard.errors.packageLimit"));
          return;
        }

        if (refreshCredits) refreshCredits();

        setStrategy(response.data.strategy);
        setBusinessContext(params.business_context);
        setTimeRange(params.time_period);
        setSelectedAccounts(selectedAccountIds);
        // Carry the platform along: the generator's slots come back without an
        // account id, and finalize has to map each one to a real account.
        setSelectedAccountDetails(
          accounts
            .filter((a) => selectedAccountIds.includes(a.id))
            .map((a) => ({ id: a.id, social_type: a.social_type, name: a.name })),
        );

        nextStep();
      } else {
        setLocalError(response?.error || i18n.t("aiCalendarWizard.errors.analysisFailed"));
      }
    } catch (error) {
      console.error("Analysis failed:", error);
      setLocalError(error.message || i18n.t("aiCalendarWizard.errors.generic"));
    } finally {
      setLoading(false);
    }
  };

  const sectionCardSx = {
    borderRadius: 2,
    bgcolor: "var(--background-menu-list)",
    color: "var(--text-color)",
    borderColor: "var(--border-color)",
    backgroundImage: "none",
  };
  const sectionTitleSx = { mb: 2, fontWeight: 600, color: "var(--text-color)" };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={i18n.language}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            {i18n.t("aiCalendarWizard.step1.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {i18n.t("aiCalendarWizard.step1.description")}
          </Typography>
        </Box>

        {localError && (
          <Alert severity="error" onClose={() => setLocalError(null)}>
            {localError}
          </Alert>
        )}

        {/* Objectives */}
        <Card variant="outlined" sx={sectionCardSx}>
          <CardContent>
            <Typography variant="subtitle1" sx={sectionTitleSx}>
              {i18n.t("aiCalendarWizard.step1.objectives")} *
            </Typography>
            <FormGroup>
              {objectiveOptions.map((opt) => (
                <FormControlLabel
                  key={opt.value}
                  control={
                    <Checkbox
                      checked={objectives.includes(opt.value)}
                      onChange={() => handleObjectiveToggle(opt.value)}
                    />
                  }
                  label={opt.label}
                />
              ))}
            </FormGroup>
          </CardContent>
        </Card>

        {/* Time Period */}
        <Card variant="outlined" sx={sectionCardSx}>
          <CardContent>
            <Typography variant="subtitle1" sx={sectionTitleSx}>
              {i18n.t("aiCalendarWizard.step1.timePeriod")} *
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={timePeriodType === "custom" ? 4 : 6}>
                <FormControl fullWidth>
                  <InputLabel>{i18n.t("aiCalendarWizard.step1.periodType")}</InputLabel>
                  <Select
                    value={timePeriodType}
                    label={i18n.t("aiCalendarWizard.step1.periodType")}
                    onChange={(e) => setTimePeriodType(e.target.value)}
                  >
                    <MenuItem value="week">{i18n.t("aiCalendarWizard.step1.week")}</MenuItem>
                    <MenuItem value="month">{i18n.t("aiCalendarWizard.step1.month")}</MenuItem>
                    <MenuItem value="custom">{i18n.t("aiCalendarWizard.step1.custom")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={timePeriodType === "custom" ? 4 : 6}>
                <DatePicker
                  label={i18n.t("aiCalendarWizard.step1.startDate")}
                  value={startDate}
                  onChange={(value) => setStartDate(value)}
                  format="DD.MM.YYYY"
                  slotProps={{
                    textField: { fullWidth: true, required: true },
                  }}
                />
              </Grid>
              {timePeriodType === "custom" && (
                <Grid item xs={12} sm={4}>
                  <DatePicker
                    label={i18n.t("aiCalendarWizard.step1.endDate")}
                    value={endDate}
                    onChange={(value) => setEndDate(value)}
                    format="DD.MM.YYYY"
                    minDate={startDate || undefined}
                    slotProps={{
                      textField: { fullWidth: true, required: true },
                    }}
                  />
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* Business Context */}
        <Card variant="outlined" sx={sectionCardSx}>
          <CardContent>
            <Typography variant="subtitle1" sx={sectionTitleSx}>
              {i18n.t("aiCalendarWizard.step1.businessContext")} *
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label={i18n.t("aiCalendarWizard.step1.industry")}
                  fullWidth
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  placeholder={i18n.t("aiCalendarWizard.step1.industryPlaceholder")}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={i18n.t("aiCalendarWizard.step1.targetAudience")}
                  fullWidth
                  multiline
                  rows={2}
                  value={targetAudience}
                  onChange={(e) => setTargetAudience(e.target.value)}
                  placeholder={i18n.t("aiCalendarWizard.step1.audiencePlaceholder")}
                  required
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Account Selection */}
        <Card variant="outlined" sx={sectionCardSx}>
          <CardContent>
            <Typography variant="subtitle1" sx={sectionTitleSx}>
              {i18n.t("aiCalendarWizard.step1.selectAccounts")} *
            </Typography>
            <FormControl fullWidth required>
              <InputLabel>{i18n.t("aiCalendarWizard.step1.accounts")}</InputLabel>
              <Select
                multiple
                value={selectedAccountIds}
                label={i18n.t("aiCalendarWizard.step1.accounts")}
                onChange={(e) => setSelectedAccountIds(e.target.value)}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((id) => {
                      const account = accounts.find((a) => a.id === id);
                      return (
                        <Chip
                          key={id}
                          label={account ? account.username : id}
                          size="small"
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {accounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.username} ({account.social_type})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </CardContent>
        </Card>

        {/* Actions */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={loading || isTrial}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {loading
              ? i18n.t("aiCalendarWizard.analyzing")
              : i18n.t("aiCalendarWizard.next")}
          </Button>
        </Box>

        <BuyCreditsModal
          open={buyCreditsOpen}
          onClose={() => setBuyCreditsOpen(false)}
          isTrial={isTrial}
        />
      </Stack>
    </LocalizationProvider>
  );
}
